import { useState } from 'react';
import { Pencil } from 'lucide-react';
import { validateEmail } from 'shared';

interface EditStaffModalProps {
  name: string;
  email: string;
  loading: boolean;
  onSave: (data: { name: string; email: string }) => void;
  onClose: () => void;
}

/** The super admin editing another member of staff's name and email. */
export default function EditStaffModal({ name, email, loading, onSave, onClose }: EditStaffModalProps) {
  const [form, setForm] = useState({ name, email });
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = { name: form.name.trim(), email: form.email.trim() };
    const found: { name?: string; email?: string } = {};
    if (!trimmed.name) found.name = 'O nome é obrigatório.';
    else if (trimmed.name.length > 255) found.name = 'Máximo de 255 caracteres.';
    if (!validateEmail(trimmed.email).valid) found.email = 'Indica um email válido.';

    if (found.name || found.email) {
      setErrors(found);
      return;
    }
    onSave(trimmed);
  };

  const inputClass =
    'w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-50';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-staff-title"
    >
      <form
        onSubmit={handleSave}
        noValidate
        className="bg-card border border-border rounded-xl shadow-xl p-6 w-full max-w-sm mx-4"
      >
        <div className="flex items-center gap-2 mb-4">
          <Pencil className="w-4 h-4 text-primary" />
          <h2 id="edit-staff-title" className="text-base font-semibold text-foreground">
            Editar conta
          </h2>
        </div>

        <div className="space-y-3">
          <div className="space-y-1">
            <label htmlFor="edit-staff-name" className="text-xs font-medium text-muted-foreground">
              Nome
            </label>
            <input
              id="edit-staff-name"
              type="text"
              value={form.name}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, name: e.target.value }));
                setErrors((prev) => ({ ...prev, name: undefined }));
              }}
              disabled={loading}
              maxLength={255}
              className={inputClass}
              aria-invalid={!!errors.name}
              autoFocus
            />
            {errors.name && (
              <p className="text-xs text-destructive" role="alert">
                {errors.name}
              </p>
            )}
          </div>

          <div className="space-y-1">
            <label htmlFor="edit-staff-email" className="text-xs font-medium text-muted-foreground">
              Email
            </label>
            <input
              id="edit-staff-email"
              type="email"
              value={form.email}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, email: e.target.value }));
                setErrors((prev) => ({ ...prev, email: undefined }));
              }}
              disabled={loading}
              maxLength={255}
              className={inputClass}
              aria-invalid={!!errors.email}
            />
            {errors.email && (
              <p className="text-xs text-destructive" role="alert">
                {errors.email}
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-accent transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 rounded-lg bg-primary hover:bg-primary/90 text-sm font-medium text-white transition-colors disabled:opacity-50"
          >
            {loading ? 'A gravar...' : 'Gravar'}
          </button>
        </div>
      </form>
    </div>
  );
}
